/** Controller for the API-tokens panel: lifecycle, CRUD, modal wiring.
 *
 * Layer: `controller` per docs/design/spa-account-extraction-260524.md §3.3.
 *
 * Covers:
 *   - loadApiTokens          — paint the API-tokens panel
 *   - openApiTokenModal      — open a modal by injecting HTML into the slot
 *   - closeApiTokenModal     — clear the modal slot
 *   - submitGenerateToken    — POST /api/account/tokens
 *   - submitDeleteToken      — DELETE /api/account/tokens/:id
 *   - submitRevokeAllTokens  — DELETE /api/account/tokens
 *   - copyRevealedToken      — clipboard copy from the reveal modal
 *   - attach()               — bind delegated click + expiry-toggle +
 *                              Escape-keydown listeners
 *
 * Class shape per CLAUDE.md rule #11a: `_apiTokensCachedMax` is mutable
 * module-private state; class is the correct shape here.
 *
 * Constructor + attach() pattern: constructor takes deps only; attach()
 * binds document listeners. Enables headless test construction without DOM.
 *
 * Extracted from web/static/account.html lines 510-714 in Phase AC.5.
 */
import { renderApiTokensEmpty, renderApiTokensTable, renderGenerateModal, renderRevealModal, renderDeleteModal, renderRevokeAllModal, } from './view-api-tokens.js';
const API_TOKENS_MAX_PER_USER_DEFAULT = 10;
export class ApiTokensController {
    _apiTokensCachedMax = API_TOKENS_MAX_PER_USER_DEFAULT;
    _esc;
    _authFetch;
    _doc;
    _nav;
    constructor(deps) {
        this._esc = deps.esc;
        this._authFetch = deps.authFetch;
        this._doc = deps.doc;
        this._nav = deps.nav;
    }
    /** Bind all document-level listeners. Call once after DOMContentLoaded. */
    attach() {
        // Delegated handler for all API-token interactions (data-action routing).
        this._doc.addEventListener('click', (e) => {
            const tgt = e.target?.closest('[data-action]');
            if (!tgt)
                return;
            const action = tgt.dataset['action'];
            if (action === 'api-token-generate') {
                e.preventDefault();
                this.openApiTokenModal(renderGenerateModal());
            }
            else if (action === 'api-token-modal-close') {
                e.preventDefault();
                this.closeApiTokenModal();
            }
            else if (action === 'api-token-gen-submit') {
                e.preventDefault();
                void this.submitGenerateToken();
            }
            else if (action === 'api-token-delete') {
                e.preventDefault();
                this.openApiTokenModal(renderDeleteModal(this._esc, tgt.dataset['tokenId'] ?? '', tgt.dataset['tokenName'] ?? '', tgt.dataset['tokenPrefix'] ?? ''));
            }
            else if (action === 'api-token-delete-confirm') {
                e.preventDefault();
                void this.submitDeleteToken(tgt.dataset['tokenId'] ?? '');
            }
            else if (action === 'api-token-revoke-all') {
                e.preventDefault();
                this.openApiTokenModal(renderRevokeAllModal());
            }
            else if (action === 'api-token-revoke-all-confirm') {
                e.preventDefault();
                void this.submitRevokeAllTokens();
            }
            else if (action === 'api-token-copy') {
                e.preventDefault();
                void this.copyRevealedToken(tgt);
            }
        });
        // Expiry option toggle — group radio behaviour.
        this._doc.addEventListener('click', (e) => {
            const opt = e.target?.closest('.expiry-option');
            if (!opt)
                return;
            const parent = opt.parentElement;
            if (!parent)
                return;
            parent.querySelectorAll('.expiry-option').forEach(b => {
                b.classList.remove('selected');
                b.setAttribute('aria-pressed', 'false');
            });
            opt.classList.add('selected');
            opt.setAttribute('aria-pressed', 'true');
        });
        // Scope option toggle — group radio behaviour (byo-agent-auth §3.4).
        this._doc.addEventListener('click', (e) => {
            const opt = e.target?.closest('.scope-option');
            if (!opt)
                return;
            const parent = opt.parentElement;
            if (!parent)
                return;
            parent.querySelectorAll('.scope-option').forEach(b => {
                b.classList.remove('selected');
                b.setAttribute('aria-checked', 'false');
            });
            opt.classList.add('selected');
            opt.setAttribute('aria-checked', 'true');
        });
        // Close modal on Escape.
        this._doc.addEventListener('keydown', (e) => {
            if (e.key !== 'Escape')
                return;
            const slot = this._doc.getElementById('api-tokens-modals');
            if (slot && slot.children.length > 0)
                this.closeApiTokenModal();
        });
    }
    async loadApiTokens() {
        const body = this._doc.getElementById('api-tokens-body');
        const count = this._doc.getElementById('api-tokens-count');
        if (!body)
            return;
        try {
            const resp = await this._authFetch('/api/account/tokens');
            if (resp.status === 401) {
                body.innerHTML = '<p style="font-size:12px;color:var(--text-muted)">Please sign in to manage API tokens.</p>';
                if (count)
                    count.textContent = '';
                return;
            }
            if (!resp.ok) {
                body.innerHTML = `<p style="font-size:12px;color:var(--error)">Failed to load tokens (HTTP ${resp.status}).</p>`;
                if (count)
                    count.textContent = '';
                return;
            }
            const data = await resp.json();
            this._apiTokensCachedMax = data.max_per_user || API_TOKENS_MAX_PER_USER_DEFAULT;
            const tokens = Array.isArray(data.tokens) ? data.tokens : [];
            const activeCount = tokens
                .filter(t => !t.revoked_at && !t.is_expired).length;
            if (count)
                count.textContent = `${activeCount} / ${this._apiTokensCachedMax} tokens`;
            body.innerHTML = tokens.length === 0
                ? renderApiTokensEmpty()
                : renderApiTokensTable(this._esc, tokens);
        }
        catch (e) {
            window.adatool?.log?.warn('[account] api-tokens fetch failed:', e);
            body.innerHTML = '<p style="font-size:12px;color:var(--error)">Could not load tokens. Refresh to retry.</p>';
        }
    }
    openApiTokenModal(html) {
        const slot = this._doc.getElementById('api-tokens-modals');
        if (!slot)
            return;
        slot.innerHTML = html;
        const focusable = slot.querySelector('input, button, [tabindex]');
        if (focusable)
            try {
                focusable.focus();
            }
            catch (_e) { /* best-effort */ }
    }
    closeApiTokenModal() {
        const slot = this._doc.getElementById('api-tokens-modals');
        if (slot)
            slot.innerHTML = '';
    }
    async submitGenerateToken() {
        const input = this._doc.getElementById('api-token-name-input');
        const errEl = this._doc.getElementById('api-token-gen-error');
        if (!input)
            return;
        const name = (input.value || '').trim();
        if (!name) {
            if (errEl) {
                errEl.textContent = 'Token name is required.';
                errEl.hidden = false;
            }
            input.focus();
            return;
        }
        const selected = this._doc.querySelector('.expiry-option.selected');
        const days = selected ? parseInt(selected.dataset['expiryDays'] || '0', 10) : 0;
        let expiresAt = null;
        if (days > 0) {
            const d = new Date(Date.now() + days * 86400 * 1000);
            expiresAt = d.toISOString();
        }
        const scopeEl = this._doc.querySelector('.scope-option.selected');
        const scope = scopeEl?.dataset['tokenScope'] || 'doc-edit:apply';
        try {
            const resp = await this._authFetch('/api/account/tokens', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ name, expires_at: expiresAt, scope }),
            });
            const data = await resp.json().catch(() => ({}));
            if (!resp.ok) {
                if (errEl) {
                    errEl.textContent = data.detail || `Failed (HTTP ${resp.status}).`;
                    errEl.hidden = false;
                }
                return;
            }
            this.closeApiTokenModal();
            this.openApiTokenModal(renderRevealModal(this._esc, data));
        }
        catch (e) {
            window.adatool?.log?.error('[account] generate token failed:', e);
            if (errEl) {
                errEl.textContent = 'Network error. Please retry.';
                errEl.hidden = false;
            }
        }
    }
    async submitDeleteToken(tokenId) {
        if (!tokenId)
            return;
        try {
            const resp = await this._authFetch('/api/account/tokens/' + encodeURIComponent(tokenId), { method: 'DELETE' });
            if (!resp.ok && resp.status !== 404) {
                alert('Failed to delete token (HTTP ' + resp.status + ').');
                return;
            }
        }
        catch (e) {
            window.adatool?.log?.error('[account] delete token failed:', e);
            alert('Network error. Please retry.');
            return;
        }
        this.closeApiTokenModal();
        void this.loadApiTokens();
    }
    async submitRevokeAllTokens() {
        try {
            const resp = await this._authFetch('/api/account/tokens', { method: 'DELETE' });
            if (!resp.ok) {
                alert('Failed to revoke tokens (HTTP ' + resp.status + ').');
                return;
            }
        }
        catch (e) {
            window.adatool?.log?.error('[account] revoke-all failed:', e);
            alert('Network error. Please retry.');
            return;
        }
        this.closeApiTokenModal();
        void this.loadApiTokens();
    }
    async copyRevealedToken(btn) {
        const code = this._doc.getElementById('api-token-revealed-value');
        if (!code)
            return;
        const text = code.textContent || '';
        try {
            if (this._nav.clipboard && this._nav.clipboard.writeText) {
                await this._nav.clipboard.writeText(text);
            }
            else {
                const ta = this._doc.createElement('textarea');
                ta.value = text;
                this._doc.body.appendChild(ta);
                ta.select();
                this._doc.execCommand('copy');
                ta.remove();
            }
            if (btn) {
                btn.classList.add('copied');
                btn.textContent = '✓ Copied!';
            }
        }
        catch (e) {
            window.adatool?.log?.warn('[account] clipboard copy failed:', e);
        }
    }
}
